import { produce } from 'immer';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import mailImg from '../../../../../common/assets/images/marketing/subscribe-icon.png';
import Button from '../../../../../common/widgets/Button';
import Icons from '../../../../../common/widgets/Icons';
import { CustomerInfoContext } from '../../../contexts/CustomerInfoContext';
import { OptInSourceTypes, OptInTypes } from '../../../enums';
import { resources } from '../../../resources/resources';
import { optInService } from '../../../services/opt-in';
import { showNewsletterErrorToast } from './NewsletterBanner';

type Status = 'idle' | 'loading' | 'success' | 'error';

export default function NewsletterCheckoutBanner() {
  const { t: translate } = useTranslation();
  const { customerInfo, setCustomerInfo } = useContext(CustomerInfoContext);
  const isProcessingRef = useRef(false);
  const [status, setStatus] = useState<Status>('idle');

  const handleConsent = async () => {
    if (isProcessingRef.current) return;

    isProcessingRef.current = true;
    setStatus('loading');

    try {
      const response = await optInService.consent(
        OptInTypes.NewsletterMarketing,
        true,
        OptInSourceTypes.PaymentConfirmation,
      );

      if (response.hasErrors) {
        showNewsletterErrorToast(
          translate(resources.newsletter.banners.error.title),
          translate(resources.newsletter.banners.error.subtitle),
        );
        setStatus('error');
      } else {
        setStatus('success');
      }
    } finally {
      isProcessingRef.current = false;
    }
  };

  useEffect(() => {
    return () => {
      if (status === 'success') {
        setCustomerInfo(
          produce(customerInfo, draft => {
            draft.showNewsletterBanners = false;
            draft.showNewsletterPopup = false;
          }),
        );
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  if (status === 'success') {
    return (
      <div className="newsletter-checkout-banner success">
        <div className="content">
          <div className="icon-wrapper">
            <img src={mailImg} alt="" className="icon" />
          </div>
          <div>
            <h3 className="title">
              {translate(resources.newsletter.banners.success.title)}{' '}
              <Icons.PartyEmoji />
            </h3>

            <p className="text">
              {translate(resources.newsletter.banners.success.subtitle)}
            </p>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="newsletter-checkout-banner">
      <div className="icon-wrapper">
        <img src={mailImg} alt="" className="icon" />
      </div>

      <h3 className="title">
        {translate(resources.newsletter.banners.checkoutDescription)}
      </h3>

      <div className="text">
        <small>
          {translate(resources.newsletter.banners.footer.text1)}{' '}
          <Link
            to="/politica-prelucrare-date-personale"
            target="_blank"
            rel="noopener noreferrer"
          >
            {translate(resources.newsletter.banners.footer.privacyPolicyLink)}
          </Link>
          {translate(resources.newsletter.banners.footer.text2)}{' '}
          <Link to="/my-account/preferences">
            {translate(
              resources.newsletter.banners.footer.communicationPreferencesLink,
            )}
          </Link>
          {customerInfo.languageCode === 'ro' ? '' : ' '}
          {translate(resources.newsletter.banners.footer.text3)}
        </small>
      </div>

      <div className="actions">
        <Button
          className="btn btn-solid"
          loading={status === 'loading'}
          onClick={handleConsent}
        >
          {translate(resources.newsletter.banners.action)}
        </Button>
      </div>
    </div>
  );
}
